import { cn } from "@/lib/utils";
import { forwardRef } from "react";

// ============================================
// 타입 정의
// ============================================

type SegmentSize = "base" | "small";

interface SegmentItemData<T extends string = string> {
  value: T;
  label: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
}

interface SegmentControlProps<T extends string = string> {
  items: SegmentItemData<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: SegmentSize;
  className?: string;
}

interface SegmentItemProps {
  selected?: boolean;
  size?: SegmentSize;
  disabled?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

// ============================================
// 스타일 정의
// ============================================

const itemBaseStyles =
  "group relative inline-flex items-center justify-center rounded-small transition-colors overflow-hidden cursor-pointer";

const sizeStyles: Record<SegmentSize, string> = {
  base: "h-9 px-4 gap-1.5",
  small: "h-7 px-3 gap-1",
};

const textStyles: Record<SegmentSize, string> = {
  base: "body-2-normal-medium",
  small: "label-2-medium",
};

// 선택/미선택 모두 border를 가져야 레이아웃 시프트 방지
const selectedStyles = "bg-neutral-900 text-white border border-transparent";
const unselectedStyles = "border border-neutral-200 text-neutral-900";
const disabledStyles = "opacity-50 cursor-not-allowed";

// 오버레이 효과 (hover 3%, pressed 6%)
const overlaySelectedStyles = "bg-white";
const overlayUnselectedStyles = "bg-black";

// ============================================
// SegmentItem 컴포넌트
// ============================================

const SegmentItem = forwardRef<HTMLButtonElement, SegmentItemProps>(
  (
    {
      selected = false,
      size = "base",
      disabled = false,
      leftIcon,
      rightIcon,
      children,
      onClick,
      className,
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          itemBaseStyles,
          sizeStyles[size],
          selected ? selectedStyles : unselectedStyles,
          disabled && disabledStyles,
          className,
        )}
      >
        {/* interaction overlay: hover 3%, pressed 6% */}
        <span
          className={cn(
            "absolute inset-0 rounded-small opacity-0 transition-opacity pointer-events-none",
            !disabled && "group-hover:opacity-[.03] group-active:opacity-6",
            selected ? overlaySelectedStyles : overlayUnselectedStyles,
          )}
          aria-hidden
        />
        {leftIcon && (
          <span className="relative size-4 flex items-center justify-center shrink-0">
            {leftIcon}
          </span>
        )}
        <span className={cn("relative whitespace-nowrap", textStyles[size])}>
          {children}
        </span>
        {rightIcon && (
          <span className="relative size-4 flex items-center justify-center shrink-0">
            {rightIcon}
          </span>
        )}
      </button>
    );
  },
);

SegmentItem.displayName = "SegmentItem";

// ============================================
// SegmentControl 컴포넌트
// ============================================

function SegmentControlInner<T extends string = string>(
  { items, value, onChange, size = "base", className }: SegmentControlProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  return (
    <div
      ref={ref}
      role="radiogroup"
      className={cn("inline-flex items-center gap-3 flex-nowrap", className)}
    >
      {items.map((item) => (
        <SegmentItem
          key={item.value}
          selected={value === item.value}
          size={size}
          disabled={item.disabled}
          leftIcon={item.leftIcon}
          rightIcon={item.rightIcon}
          onClick={() => {
            if (!item.disabled) {
              onChange(item.value);
            }
          }}
        >
          {item.label}
        </SegmentItem>
      ))}
    </div>
  );
}

// 제네릭 타입을 지원하는 forwardRef
export const SegmentControl = forwardRef(SegmentControlInner) as <
  T extends string = string,
>(
  props: SegmentControlProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;

export { SegmentItem };
export type { SegmentControlProps, SegmentItemData, SegmentItemProps };
